import type { EsProduct } from '~layers/app/types/elastic'

export function useQuickSearch() {
  const search = useState('search', () => '')
  const searchDialogOpen = useState('searchDialogOpen', () => false)

  // Request
  const data = ref()
  const loading = ref(false)
  const error = ref()

  const recentSearches = useLocalStorage<string[]>('recentSearches', [], { initOnMounted: true })

  const variables = computed(() => ({
    search: search.value,
  }))
  const enabled = computed(() => search.value.length > 2)

  const searchRedirect = () => {
    if (!search.value) {
      return
    }
    const router = useRouter()

    const matching = [...categories.value, ...pages.value]
      .find(({ label }: { label: string }) => label.toLowerCase() === search.value.toLowerCase())
    if (matching) {
      router.push({ path: '/' + matching.url + '/' })
    }
    else {
      router.push({ path: '/search/', query: { q: search.value } })
    }
  }

  const products = computed<EsProduct[]>(() => data.value?.products.products)
  const categories = computed(() => data.value?.categories.urls || [])
  const pages = computed(() => data.value?.pages.urls || [])

  const totalProducts = computed(() => data.value?.products.totalCount || 0)

  const hasResults = computed(() => {
    return totalProducts.value > 0 || categories.value.length > 0 || pages.value.length > 0
  })

  watch(data, () => {
    if (totalProducts.value > 0) {
      const newRecentSearches = new Set([search.value, ...recentSearches.value])
      recentSearches.value = Array.from(newRecentSearches).slice(0, 5)
    }
  })

  watchDebounced(search, () => {
    if (!enabled.value) {
      data.value = null
      loading.value = false
      return
    }

    loading.value = true
    error.value = undefined

    useGraphqlQuery('quickSearch', variables.value, useGraphqlDefaultClient())
      .then((response) => {
        if (response.error) {
          error.value = response.error
          data.value = null
        }
        else {
          data.value = response.data
        }
      })
      .catch((err) => {
        error.value = err
        data.value = null
      })
      .finally(() => {
        loading.value = false
      })
  },
  { debounce: 500, maxWait: 1500 },
  )

  return {
    data,
    search,
    searchDialogOpen,
    recentSearches,

    error,
    loading,

    hasResults,
    categories,
    pages,
    products,
    totalProducts,

    searchRedirect,
  }
}
